import { createFileRoute, Link } from "@tanstack/react-router";
import type { FormEvent } from "react";
import { useId, useState } from "react";
import {
	AuthError,
	AuthHeader,
	AuthPageShell,
	AuthSwitchLink,
	Divider,
	GoogleButton,
	PasswordInput,
	SubmitButton,
	TextInput,
} from "@/components/auth/auth-ui";
import { LoadingScreen } from "@/components/LoadingScreen";
import { useWebHaptics } from "@/lib/haptics";
import {
	clearPendingAuthProvider,
	getLastAuthProvider,
	setLastAuthProvider,
	setPendingAuthProvider,
} from "@/lib/last-auth-provider";
import { noIndexMeta } from "@/lib/seo";
import { useSession } from "../hooks/auth-hooks";
import { signIn } from "../lib/auth-client";

export const Route = createFileRoute("/login")({
	component: LoginPage,
	head: () => ({
		meta: [{ title: "AgentFramer | Log in" }, ...noIndexMeta()],
	}),
});

export function LoginPage() {
	const { trigger } = useWebHaptics();
	const { session, user, isPending, refetch } = useSession();
	const [email, setEmail] = useState("");
	const [password, setPassword] = useState("");
	const [showPassword, setShowPassword] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const [emailLoading, setEmailLoading] = useState(false);
	const [googleLoading, setGoogleLoading] = useState(false);
	const emailId = useId();
	const passwordId = useId();
	const errorId = useId();
	const redirect = getRedirectParam();
	const postAuthUrl = redirect || "/dashboard";
	const lastProvider = getLastAuthProvider();

	if (!isPending && session && user) {
		if (typeof window !== "undefined") window.location.assign(postAuthUrl);
		return null;
	}
	if (isPending) return <LoadingScreen />;

	const handleEmailLogin = async (event: FormEvent<HTMLFormElement>) => {
		event.preventDefault();
		setError(null);
		setEmailLoading(true);

		try {
			const result = await signIn.email({ email, password });
			if (result.error) {
				setError(result.error.message || "Failed to sign in");
				return;
			}

			setLastAuthProvider("email");
			trigger("success");
			await refetch();
			window.location.assign(postAuthUrl);
		} catch (err) {
			setError(err instanceof Error ? err.message : "An error occurred");
		} finally {
			setEmailLoading(false);
		}
	};

	const handleGoogleSignIn = async () => {
		setError(null);
		setGoogleLoading(true);
		setPendingAuthProvider("google");
		trigger("success");

		try {
			await signIn.social({ provider: "google", callbackURL: postAuthUrl });
		} catch (err) {
			clearPendingAuthProvider();
			setError(
				err instanceof Error ? err.message : "Failed to sign in with Google",
			);
			setGoogleLoading(false);
		}
	};

	return (
		<AuthPageShell>
			<AuthHeader
				eyebrow="Sign in"
				headingId="login-heading"
				title="Welcome back"
				subtitle="Your agents are waiting."
			/>

			{error && <AuthError id={errorId}>{error}</AuthError>}

			<GoogleButton
				loading={googleLoading}
				lastProvider={lastProvider}
				onClick={handleGoogleSignIn}
			/>
			<Divider />

			<form
				onSubmit={handleEmailLogin}
				aria-labelledby="login-heading"
				className="space-y-4"
			>
				<TextInput
					id={emailId}
					label="Email"
					type="email"
					autoComplete="email"
					value={email}
					onChange={setEmail}
					placeholder="name@company.com"
					errorId={error ? errorId : undefined}
				/>
				<PasswordInput
					id={passwordId}
					autoComplete="current-password"
					value={password}
					onChange={setPassword}
					showPassword={showPassword}
					onTogglePassword={() => setShowPassword((value) => !value)}
					placeholder="Enter your password"
					errorId={error ? errorId : undefined}
					trailingLabel={
						<Link
							to="/forgot-password"
							className="text-xs font-medium text-brand-accent transition-colors hover:text-foreground"
						>
							Forgot password?
						</Link>
					}
				/>
				<SubmitButton loading={emailLoading} loadingLabel="Signing in...">
					Sign in
				</SubmitButton>
				{lastProvider === "email" && (
					<p className="text-center text-xs text-brand-accent">
						✓ You last signed in with email
					</p>
				)}
			</form>

			<AuthSwitchLink
				question="Don't have an account?"
				linkText="Sign up"
				to="/signup"
				redirect={redirect}
			/>
		</AuthPageShell>
	);
}

function getRedirectParam() {
	if (typeof window === "undefined") return null;
	return new URLSearchParams(window.location.search).get("redirect");
}
